/**
 * Shared API base module.
 * Provides singleton CSRF initialization and stable client ID cookie
 * to prevent session race conditions when multiple API modules make
 * concurrent requests on first page load.
 */

import { getTestToken } from '../utils/testMode';
import { getActiveLanguage, t } from '../i18n';

// Use environment variable for API URL, fallback to relative path for web
const API_BASE = process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api`
    : '/api';

// Singleton promise for CSRF initialization
let _csrfPromise = null;

/**
 * Get CSRF token from cookie.
 */
function getCSRFToken() {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

/**
 * Ensure a stable client ID cookie exists before any API calls.
 * This cookie is set synchronously so ALL concurrent fetch requests
 * will include it, preventing the Django session race condition where
 * concurrent requests each create a different session_user_id.
 */
function ensureClientId() {
    const name = 'wl_client_id';
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                return; // Already exists
            }
        }
    }
    // Generate a UUID v4
    const clientId = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    // 20-year expiry, matching Django session. We mark the cookie Secure on
    // any HTTPS page so it never travels in cleartext over a downgrade. We
    // can't set HttpOnly here (the browser only honours that flag when
    // cookies are set by the server), so the JS-set version is the
    // pragmatic fallback. SameSite=Lax is fine — this is not an auth token,
    // just a stable client identifier.
    const isHttps = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
    const secureFlag = isHttps ? '; Secure' : '';
    document.cookie = `${name}=${clientId}; max-age=${20 * 365 * 24 * 60 * 60}; path=/; SameSite=Lax${secureFlag}`;
}

/**
 * Shared CSRF initialization - singleton promise ensures only ONE
 * /api/csrf/ request goes out even if multiple modules call this concurrently.
 */
async function ensureCSRF() {
    // Always ensure client ID cookie is set first (synchronous)
    ensureClientId();

    if (getCSRFToken()) return;
    if (!_csrfPromise) {
        _csrfPromise = fetch(`${API_BASE}/csrf/`, { credentials: 'include' })
            .then(() => {})
            .catch(() => { _csrfPromise = null; });
    }
    return _csrfPromise;
}

/**
 * Check if running inside Capacitor native shell.
 */
function _isCapacitor() {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

/**
 * Shared API request helper.
 */
// Carries the HTTP status so callers can tell a server rejection from a transient failure.
function httpError(message, status) {
    const err = new Error(message);
    err.status = status;
    return err;
}

async function apiRequest(endpoint, options = {}) {
    await ensureCSRF();

    const headers = {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCSRFToken(),
        // Server-side messages follow the UI language, not the browser's Accept-Language.
        'X-WL-Language': getActiveLanguage(),
    };

    // Identify Capacitor native requests for server-side CSRF exemption
    if (_isCapacitor()) {
        headers['X-WL-Client'] = 'capacitor';
    }

    // Stamp the real platform on EVERY request so product analytics is
    // reliable. The native iOS/Android apps load the live site through a
    // Capacitor WebView (server.url), so their User-Agent AND Origin are
    // indistinguishable from mobile web — the backend cannot infer the
    // platform from request headers. Capacitor knows it (getPlatform() ->
    // 'ios' | 'android' | 'web'), so the client must tell the server. The
    // backend trusts this header first (get_platform_from_request), falling
    // back to UA only when it's absent.
    headers['X-WL-Platform'] = (
        typeof window !== 'undefined'
        && window.Capacitor
        && typeof window.Capacitor.getPlatform === 'function'
    ) ? window.Capacitor.getPlatform() : 'web';

    // Test mode: when the owner has activated it (?test=<token>), tell the
    // backend so this session's Player is flagged is_test and excluded from
    // analytics. Read fresh each request so disabling takes effect immediately.
    const _testToken = getTestToken();
    if (_testToken) {
        headers['X-WL-Test-Token'] = _testToken;
    }

    const defaultOptions = {
        headers,
        credentials: 'include',
    };

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers,
        },
    });

    // Auth endpoints return 401/403 with meaningful error payloads
    // (e.g. "Invalid credentials", "email_unverified") — don't intercept those.
    const isAuthEndpoint = endpoint.startsWith('/auth/');

    // Detect session expiry (401/403) for non-auth endpoints
    if ((response.status === 401 || response.status === 403) && !isAuthEndpoint) {
        window.dispatchEvent(new CustomEvent('wl:session-expired'));
        let data;
        try {
            data = await response.json();
        } catch {
            data = {};
        }
        throw httpError(data.error || t('errors.sessionExpired'), response.status);
    }

    let data;
    try {
        data = await response.json();
    } catch {
        throw httpError(t('errors.server'), response.status);
    }

    // For auth endpoints, return data as-is so callers can handle
    // specific error fields (email_unverified, needsUsername, etc.)
    if (isAuthEndpoint) {
        return data;
    }

    if (!response.ok) {
        throw httpError(data.error || t('errors.http', { status: response.status }), response.status);
    }

    return data;
}

export { getCSRFToken, ensureCSRF, apiRequest, API_BASE };
